import { useState, useCallback } from 'react'
import { storageManager, UploadResult, StorageFile } from '../lib/storage'

export interface UseStorageOptions {
  onProgress?: (progress: number) => void
  onSuccess?: (result: any) => void
  onError?: (error: Error) => void
}

export function useStorage(options: UseStorageOptions = {}) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)

  const uploadFile = useCallback(async (
    bucket: 'videos' | 'outputs',
    file: File,
    path?: string
  ): Promise<UploadResult | null> => {
    setIsLoading(true)
    setError(null)
    setUploadProgress(0)

    try {
      const result = await storageManager.uploadFile(
        bucket,
        file,
        path,
        (progress) => {
          setUploadProgress(progress)
          options.onProgress?.(progress)
        }
      )

      options.onSuccess?.(result)
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Upload failed')
      setError(error)
      options.onError?.(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [options])

  const downloadFile = useCallback(async (
    bucket: 'videos' | 'outputs',
    path: string
  ): Promise<Blob | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await storageManager.downloadFile(bucket, path)
      options.onSuccess?.(result)
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Download failed')
      setError(error)
      options.onError?.(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [options])

  const deleteFile = useCallback(async (
    bucket: 'videos' | 'outputs',
    path: string
  ): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await storageManager.deleteFile(bucket, path)
      options.onSuccess?.(result)
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Delete failed')
      setError(error)
      options.onError?.(error)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [options])

  const listFiles = useCallback(async (
    bucket: 'videos' | 'outputs',
    folder?: string,
    limit?: number,
    offset?: number
  ): Promise<StorageFile[]> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await storageManager.listFiles(bucket, folder, limit, offset)
      options.onSuccess?.(result)
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error('List files failed')
      setError(error)
      options.onError?.(error)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [options])

  const getFileUrl = useCallback(async (
    bucket: 'videos' | 'outputs',
    path: string,
    signed?: boolean,
    expiresIn?: number
  ): Promise<string | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await storageManager.getFileUrl(bucket, path, signed, expiresIn)
      options.onSuccess?.(result)
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Get URL failed')
      setError(error)
      options.onError?.(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [options])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const reset = useCallback(() => {
    setIsLoading(false)
    setError(null)
    setUploadProgress(0)
  }, [])

  return {
    // State
    isLoading,
    error,
    uploadProgress,
    
    // Actions
    uploadFile,
    downloadFile,
    deleteFile,
    listFiles,
    getFileUrl,
    
    // Utilities
    clearError,
    reset
  }
}
